/**
 * whatsNew — deciding which release notes a user has not seen yet.
 *
 * The notes themselves ship INSIDE the bundle (src/data/releaseNotes.json,
 * written by scripts/releaseNotes.js during the CI version bump), so this is
 * deliberately a client-only feature: no endpoint, no de-hub migration, and
 * the notes can never disagree with the build they arrived with. The running
 * build's version is `__APP_VERSION__` (see vite.config.js), which is the
 * same number CI stamped onto the entry — so "is there anything new?" is a
 * comparison between that and what this browser last acknowledged.
 *
 * WHY LOCALSTORAGE AND NOT THE SERVER. The question being answered is "has
 * the person at this screen read the notes for the build they are looking
 * at", and a wrong answer costs one extra modal. That is not worth a round
 * trip, a table, or a migration. The honest limit: the prompt reappears per
 * browser and per profile, and never appears in private mode. If we later
 * want "acknowledged once per user, everywhere", it moves server-side —
 * `readSeenVersion`/`writeSeenVersion` are the only two places that know.
 *
 * FIRST VISIT IS SILENT. Someone opening the app for the first time has no
 * stored version, and greeting them with fourteen releases of changelog is
 * noise, not news — they have not seen ANY of the app yet. So a first visit
 * records the current version and shows nothing; the notes stay reachable
 * from the user menu for anyone who wants the history.
 *
 * Pure functions in utils/ (not a hook) so they are testable without a
 * component, per the repo convention.
 */

export const SEEN_KEY = "bd.whatsNew.seenVersion";

/**
 * Compare two dotted numeric versions.
 *
 * Returns > 0 when `a` is newer, < 0 when `b` is newer, 0 when equal or
 * uncomparable. Non-numeric segments count as 0 rather than throwing: a
 * malformed version must not be able to break the nav bar.
 */
export const compareVersions = (a, b) => {
  const parts = (v) =>
    String(v || "")
      .split(".")
      .map((n) => {
        const parsed = parseInt(n, 10);
        return Number.isFinite(parsed) ? parsed : 0;
      });
  const left = parts(a);
  const right = parts(b);
  const len = Math.max(left.length, right.length);
  for (let i = 0; i < len; i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
};

/** Releases newest-first, dropping anything with no user-facing bullets. */
export const sortReleases = (releases) =>
  (Array.isArray(releases) ? releases : [])
    .filter((r) => r && Array.isArray(r.highlights) && r.highlights.length > 0)
    .slice()
    .sort((a, b) => compareVersions(b.version, a.version));

/**
 * The releases this user has not acknowledged.
 *
 * Bounded at BOTH ends on purpose:
 *   - newer than `seenVersion`  — they have already read those
 *   - not newer than `current`  — the notes file can contain an entry for a
 *     build this container is not running yet (the version bump commit lands
 *     before the image is pushed, and a page can be open across a deploy).
 *     Announcing a feature that is not on screen yet is worse than silence.
 *
 * With no `seenVersion` this returns nothing — see FIRST VISIT IS SILENT.
 */
export const releasesSince = (releases, seenVersion, current) => {
  if (!seenVersion) return [];
  return sortReleases(releases).filter(
    (r) =>
      compareVersions(r.version, seenVersion) > 0 &&
      (!current || compareVersions(r.version, current) <= 0)
  );
};

/** Last acknowledged version, or null. Never throws (private mode, quota). */
export const readSeenVersion = () => {
  try {
    return window.localStorage?.getItem(SEEN_KEY) || null;
  } catch {
    return null;
  }
};

/** Record what has been read. Never throws — failing to store is harmless. */
export const writeSeenVersion = (version) => {
  if (!version) return;
  try {
    window.localStorage?.setItem(SEEN_KEY, String(version));
  } catch {
    // localStorage unavailable; the modal will simply offer itself again.
  }
};
